/**
 * TranscriptPreview Component
 * ORCA360 - Assessment Preview UI
 * 
 * Displays list of all transcripts that will be used for assessment generation
 * Provides transparency and verification for instructors
 * 
 * MAD-16: Sprint 4, Week 1
 */

import React from 'react';

export interface Transcript {
  id: string;
  anonymized_text: string;
  duration_seconds: number | null;
  created_at: string;
  file_id?: string;
  file_name?: string | null; // NEW: Actual file name from files table
  file_type?: string | null; // NEW: File type for icon display
  processing_status?: string | null; // NEW: Filter for completed transcripts only
}

export interface TranscriptPreviewProps {
  transcripts: Transcript[];
  variant?: 'default' | 'white'; // 'white' uses white bg for embedding inside colored cards
}

/**
 * Get transcript label - prefer actual file name over heuristic.
 * For audio-sourced transcripts, show "Transcript of <audio file>"
 * since the transcript text lives in the DB, not as a separate file.
 */
function getTranscriptLabel(transcript: Transcript, index: number): string {
  if (transcript.file_name) {
    // For audio-sourced transcripts, clarify this is the transcript of that audio
    if (transcript.file_type === 'audio') {
      return `Transcript of ${transcript.file_name}`;
    }
    // For text/document uploads, the file IS the transcript
    return transcript.file_name;
  }

  // Fall back to heuristic-based labeling only if no file name
  const durationMin = transcript.duration_seconds ? Math.round(transcript.duration_seconds / 60) : 0;

  if (durationMin < 25) {
    return `BRIEFING (Part ${index + 1})`;
  } else if (durationMin > 50) {
    return `DEBRIEFING (Part ${index + 1})`;
  } else {
    return `SESSION (Part ${index + 1})`;
  }
}

/**
 * Get icon based on file type.
 * Always show a document icon since this list represents transcripts (text),
 * regardless of the original source format.
 */
function getTranscriptIcon(transcript: Transcript): string {
  if (transcript.file_type === 'text') return '📄';
  if (transcript.file_type === 'document') return '📎';
  return '📄'; // Transcripts are always text content
}

/**
 * Calculate word count from text
 */
function getWordCount(text: string): number {
  return text.split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * TranscriptPreview Component
 */
export const TranscriptPreview: React.FC<TranscriptPreviewProps> = ({ transcripts, variant = 'default' }) => {
  if (!transcripts || transcripts.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-amber-700">
          ⚠️ No transcripts found for this session. Please transcribe audio files before generating assessments.
        </p>
      </div>
    );
  }

  // Calculate totals
  const totalWords = transcripts.reduce((sum, t) => sum + getWordCount(t.anonymized_text), 0);
  const totalDuration = transcripts.reduce((sum, t) => sum + (t.duration_seconds || 0), 0);
  const totalMinutes = Math.round(totalDuration / 60);

  return (
    <div className={`rounded-lg p-3 mb-2 ${
      variant === 'white'
        ? 'bg-white border border-gray-200 mt-3 ml-10'
        : 'bg-orca-elevate/10 border border-orca-elevate/30'
    }`}>
      <h3 className={`text-xs font-semibold mb-2 flex items-center ${
        variant === 'white' ? 'text-slate-700' : 'text-navy'
      }`}>
        <span className="mr-2">📄</span>
        Transcripts to be used for assessment:
      </h3>

      <ul className="space-y-1 mb-2">
        {transcripts.map((transcript, idx) => {
          const label = getTranscriptLabel(transcript, idx);
          const icon = getTranscriptIcon(transcript);
          const wordCount = getWordCount(transcript.anonymized_text);
          const durationMin = transcript.duration_seconds ? Math.round(transcript.duration_seconds / 60) : 0;

          return (
            <li key={transcript.id} className={`flex items-center text-xs ${
              variant === 'white' ? 'text-slate-600' : 'text-orca-elevate'
            }`}>
              <span className="mr-2">{icon}</span>
              <span className="font-medium">{label}</span>
              <span className={`ml-2 ${variant === 'white' ? 'text-slate-400' : 'text-orca-elevate/80'}`}>
                ({wordCount.toLocaleString()} words, {durationMin} min)
              </span>
            </li>
          );
        })}
      </ul>

      <div className={`pt-2 border-t ${variant === 'white' ? 'border-gray-200' : 'border-orca-elevate/30'}`}>
        <p className={`text-xs ${variant === 'white' ? 'text-blue-700' : 'text-orca-elevate'}`}>
          <strong>Total:</strong> {transcripts.length} transcript{transcripts.length !== 1 ? 's' : ''},{' '}
          {totalWords.toLocaleString()} words, {totalMinutes} minutes
        </p>
        {totalWords > 50000 && (
          <p className="text-xs text-amber-600 mt-1">
            ⚠️ Large transcript ({totalWords.toLocaleString()} words). Assessment generation may take 2-3 minutes.
          </p>
        )}
      </div>
    </div>
  );
};

export default TranscriptPreview;


